import { useEffect, useMemo, useState } from "react";
import { supabase } from "@vinoga/db";
import { Search, X, Plus, Save, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { uploadImageToR2 } from "@/lib/r2-upload";

type TagRow = {
  id: string;
  slug: string;
  translations: { lv?: { name?: string }; en?: { name?: string }; ru?: { name?: string } } | null;
  color: string | null;
  /** Certification logo (EU organic, Demeter, …). When set, the storefront
   *  shows the logo ALONE on product images instead of the text chip. */
  logo_url: string | null;
  position: number;
  active: boolean;
};

export function Tags() {
  const t = useT();
  const { toast } = useToast();
  const [rows, setRows] = useState<TagRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<TagRow | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const [g, pt] = await Promise.all([
      supabase.from("tags").select("*").order("position", { ascending: true }),
      supabase.from("product_tags").select("tag_id"),
    ]);
    if (g.error) {
      toast({
        title: t("tags.errorLoading", "Failed to load tags"),
        description: g.error.message,
        variant: "destructive",
      });
    } else {
      setRows((g.data ?? []) as TagRow[]);
    }
    const c: Record<string, number> = {};
    (pt.data ?? []).forEach((r: any) => {
      c[r.tag_id] = (c[r.tag_id] ?? 0) + 1;
    });
    setCounts(c);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        r.slug.toLowerCase().includes(s) ||
        (r.translations?.lv?.name ?? "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  const save = async (g: TagRow) => {
    const { error } = await supabase
      .from("tags")
      .update({
        slug: g.slug,
        translations: g.translations,
        color: g.color,
        logo_url: g.logo_url,
        active: g.active,
      })
      .eq("id", g.id);
    if (error) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: t("common.saved", "Saved") });
    setEditing(null);
    refresh();
  };

  const remove = async (id: string) => {
    const used = counts[id] ?? 0;
    if (used > 0) {
      toast({
        title: t("tags.cantDelete", "Cannot delete"),
        description: t(
          "tags.inUse",
          `This tag is used by ${used} product(s). Untag products first.`
        ),
        variant: "destructive",
      });
      return;
    }
    if (!confirm(t("tags.confirmDelete", "Delete this tag?"))) return;
    const { error } = await supabase.from("tags").delete().eq("id", id);
    if (error) {
      toast({
        title: t("common.deleteFailed", "Delete failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    refresh();
  };

  const createNew = async () => {
    const name = prompt(t("tags.newName", "Name (LV)"));
    if (!name) return;
    const slug = name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const { error } = await supabase
      .from("tags")
      .insert({ slug, translations: { lv: { name } }, active: true });
    if (error) {
      toast({
        title: t("common.createFailed", "Create failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-xl font-bold text-foreground">
            {t("tags.title", "Tags")}
            {rows.length > 0 && (
              <span className="ml-2 font-body text-sm font-normal text-muted-foreground">
                ({rows.length})
              </span>
            )}
          </h1>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder={t("tags.searchPlaceholder", "Search by name or slug...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-[260px] pl-8 pr-8 font-body text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <Button size="sm" onClick={createNew}>
          <Plus size={16} />
          {t("tags.add", "Add tag")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        {t(
          "tags.subtitle",
          "Cross-cutting product badges (BIO, Vegan, Naturāls, Jaunumi). Categories are the navigation tree; tags are filters that overlay it."
        )}
      </p>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("tags.colSlug", "Slug")}</TableHead>
              <TableHead>{t("tags.colNameLv", "Name (LV)")}</TableHead>
              <TableHead>{t("tags.colColor", "Color")}</TableHead>
              <TableHead>{t("tags.colLogo", "Logo")}</TableHead>
              <TableHead className="text-right">
                {t("tags.colUsedIn", "Used in products")}
              </TableHead>
              <TableHead>{t("tags.colStatus", "Status")}</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  {t("common.loading", "Loading…")}
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  {t("tags.empty", "No tags.")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((g) =>
                editing?.id === g.id ? (
                  <TableRow key={g.id} className="bg-muted/30">
                    <TableCell>
                      <Input
                        value={editing.slug}
                        onChange={(e) =>
                          setEditing({ ...editing, slug: e.target.value })
                        }
                        className="h-7 font-mono text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editing.translations?.lv?.name ?? ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            translations: {
                              ...editing.translations,
                              lv: {
                                ...(editing.translations?.lv ?? {}),
                                name: e.target.value,
                              },
                            },
                          })
                        }
                        className="h-7 font-body text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Input
                          type="color"
                          value={editing.color ?? "#888888"}
                          onChange={(e) =>
                            setEditing({ ...editing, color: e.target.value })
                          }
                          className="h-7 w-12 p-1"
                        />
                        <Input
                          type="text"
                          value={editing.color ?? ""}
                          onChange={(e) =>
                            setEditing({ ...editing, color: e.target.value || null })
                          }
                          className="h-7 w-24 font-mono text-xs"
                          placeholder="#888888"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* Certification logo — when set, the storefront shows
                          the logo ALONE on product images (no text chip). */}
                      <div className="flex items-center gap-2">
                        {editing.logo_url ? (
                          <>
                            <img
                              src={editing.logo_url}
                              alt=""
                              className="h-7 w-auto rounded border border-border bg-white object-contain px-0.5"
                            />
                            <button
                              type="button"
                              className="text-xs text-muted-foreground hover:text-destructive"
                              onClick={() => setEditing({ ...editing, logo_url: null })}
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <label className="cursor-pointer rounded border border-dashed border-border px-2 py-1 font-body text-xs text-muted-foreground hover:bg-muted">
                            {uploadingLogo
                              ? t("common.uploading", "Augšupielādē…")
                              : t("tags.uploadLogo", "Augšupielādēt")}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={uploadingLogo}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setUploadingLogo(true);
                                try {
                                  const url = await uploadImageToR2(file, {
                                    bucket: "tags",
                                    preserveAspectRatio: true,
                                  });
                                  setEditing((prev) =>
                                    prev ? { ...prev, logo_url: url } : prev,
                                  );
                                } catch (err) {
                                  toast({
                                    title: t("common.uploadFailed", "Upload failed"),
                                    description: (err as Error).message,
                                    variant: "destructive",
                                  });
                                } finally {
                                  setUploadingLogo(false);
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {counts[g.id] ?? 0}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() =>
                          setEditing({ ...editing, active: !editing.active })
                        }
                        className="cursor-pointer"
                      >
                        <Badge variant={editing.active ? "default" : "secondary"}>
                          {editing.active
                            ? t("tags.statusActive", "active")
                            : t("tags.statusInactive", "inactive")}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => save(editing)}
                        className="h-7"
                      >
                        <Save size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(null)}
                        className="h-7"
                      >
                        <X size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={g.id} className="cursor-pointer hover:bg-muted/30">
                    <TableCell
                      className="font-mono text-xs"
                      onClick={() => setEditing(g)}
                    >
                      {g.slug}
                    </TableCell>
                    <TableCell className="font-medium" onClick={() => setEditing(g)}>
                      {g.translations?.lv?.name ?? "—"}
                    </TableCell>
                    <TableCell onClick={() => setEditing(g)}>
                      {g.color ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-4 w-4 rounded border border-border"
                            style={{ background: g.color }}
                          />
                          <span className="font-mono text-xs text-muted-foreground">
                            {g.color}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell onClick={() => setEditing(g)}>
                      {g.logo_url ? (
                        <img
                          src={g.logo_url}
                          alt=""
                          className="h-7 w-auto rounded border border-border bg-white object-contain px-0.5"
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="text-right text-muted-foreground"
                      onClick={() => setEditing(g)}
                    >
                      {counts[g.id] ?? 0}
                    </TableCell>
                    <TableCell onClick={() => setEditing(g)}>
                      <Badge variant={g.active ? "default" : "secondary"}>
                        {g.active
                          ? t("tags.statusActive", "active")
                          : t("tags.statusInactive", "inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(g.id)}
                        className="h-7 hover:text-destructive"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
